import { LicensePlateNoKeyboard } from '@euler/app/components/LicensePlateNoKeyboard';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationOptions } from '@react-navigation/stack';
import { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';

const kTranslateY = 200;

export const PlateNoScreen = wrapNavigatorScreen(
  ({
    licensePlateNo,
    onDone,
  }: {
    licensePlateNo?: string;
    onDone?: (value: string) => void;
  }) => {
    const navigation = useNavigation();
    const translateY = useSharedValue(kTranslateY);
    const insets = useSafeAreaInsets();
    const theme = useTheme();

    const style = useAnimatedStyle(() => ({
      transform: [{ translateY: translateY.value }],
      opacity: interpolate(translateY.value, [0, kTranslateY], [1, 0]),
    }));

    // on android, blur view is implemented via a semi transparent view
    // which can not be animated by BlurView directly, thus, we have to
    // use an independent view and animate its opacity manually instead.
    const backdropStyleAndroid = useAnimatedStyle(() => ({
      opacity: interpolate(translateY.value, [0, kTranslateY], [0.1, 0]),
    }));

    const dismiss = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    const onBackdropPress = useCallback(() => {
      translateY.value = withTiming(kTranslateY, { duration: 250 }, () => {
        runOnJS(dismiss)();
      });
    }, [dismiss, translateY]);

    const onDonePress = useCallback(
      (value: string) => {
        onDone?.(value);
        onBackdropPress();
      },
      [onBackdropPress, onDone],
    );

    const [width, setWidth] = useState<number>();

    useEffect(() => {
      if (!width) return;
      translateY.value = withTiming(0, { duration: 150 });
    }, [width, translateY]);

    return (
      <View
        css={`
          flex: 1;
        `}
        onLayout={e => setWidth(e.nativeEvent.layout.width)}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={onBackdropPress}>
          <Animated.View
            css={`
              flex: 1;
              background-color: #000;
            `}
            style={backdropStyleAndroid}
          />
        </Pressable>
        {width != null && (
          <Animated.View
            css={`
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
            `}
            style={style}
          >
            <LicensePlateNoKeyboard
              width={width}
              value={licensePlateNo}
              onDone={onDonePress}
              doneButtonText="完成"
              css={`
                border-top-width: 1px;
                border-top-color: ${theme.keyboard.topBorderColor};
                padding-bottom: ${insets.bottom}px;
                background-color: ${theme.keyboard.bgColor};
                box-shadow: 0 0 10px #ccc;
                elevation: 5;
              `}
            />
          </Animated.View>
        )}
      </View>
    );
  },
  {
    presentation: 'transparentModal',
    cardOverlayEnabled: false,
    animationEnabled: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
    headerShown: false,
  } as StackNavigationOptions,
);
